import { DestroyRef, inject, Injectable, Signal } from '@angular/core';
import { patchState, SignalState, signalState } from '@ngrx/signals';
import { Dispatcher } from '@ngrx/signals/events';
import { ResponseErrorListItemProps } from '@lentau/api/shared/monolith/types';
import {
  AuthResult,
  AuthStateProps,
  authStepByOtpCodeType,
  AuthStepEnum,
  COOKIES_TOKEN_SESSION_KEY,
  COOKIES_TOKEN_SESSION_KEY_EXPIRES,
  OtpCodeTypeEnum,
  ResponseAuthLogout,
  ResponseGetAuthTokenDto,
  UserRolesEnum,
} from '@lentau/auth/shared';
import { AnalyticsLegacyService, AnalyticsService } from '@lentau/analytics/events/shared';
import { buildEventCatalogEvent } from '@lentau/analytics/events/shared/services/event-catalog-analytics';
import { mapRegistrationStep1CompleteEvent } from 'analytic-events/Registration/services/RegistrationServiceB2B/events/RegistrationStep1Complete/RegistrationStep1Complete';
import { mapInputCodeErrorEvent } from 'analytic-events/Registration/services/RegistrationServiceB2B/events/InputCodeError/InputCodeError';
import { SendAuthCodeResponse } from 'angular-api-gateway/v1/model/sendAuthCodeResponse';
import { SessionStorageService } from '@lentau/ui-kit/core/services/storage/sessionstorage.service';
import { LocalStorageService } from '@lentau/ui-kit/core/services/storage/localstorage.service';
import { EnvironmentBrandService } from '@lentau/ui-kit/core/environments/environment-brand.service';
import { AuthApiService } from '../api/auth-api.service';
import { CookieFacade } from '@lentau/ui-kit/core/services/cookie/cookie.facade';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { standartError, VIEW_STATE } from './constants';
import { getErrorMessageFromErrorList } from '@lentau/api/shared/monolith/texting';
import { ToastService } from '@lentau/ui-kit/molecules/toast/service/toast.service';
import {
  ToasterErrorMessage,
  ToasterInfoMessage,
  ToasterSuccessMessage,
} from '@lentau/ui-kit/molecules/toast/shared/toast.types';
import { COOKIES_USER_ID_KEY } from '@lentau/auth/shared/cookie.const';
import { UserAuthorizeType } from '@lentau/auth/shared/enums';
import { BrowserApiSupportService } from '@lentau/ui-kit/core/services/browser-api-support/browser-api-support.service';
import { authSuccessWithToken } from './auth.events';
import { DateUtils } from '@lentau/ui-kit/core/utils/date.utils';
import { AuthService } from 'angular-api-gateway/v1/api/auth.service';
import { defaultApiGatewayParams } from '@lentau/core/api/gateway';
import { GetPassportGuestTokenResponse } from 'angular-api-gateway/v1/model/getPassportGuestTokenResponse';
import { REQUEST_STATE } from '@lentau/ui-kit/core/enums/request-state.enum';
import { GetPassportTokenResponse } from 'angular-api-gateway/v1/model/getPassportTokenResponse';
import { DeviceIdService } from '@lentau/core/api/shared/device-id.service';
import { CallAuthStart200Response } from 'angular-api-gateway/v1/model/callAuthStart200Response';
import { PHONE_PREFIX, COUNTRY_CODE } from '@lentau/ui-kit/core/constants/phone.const';
import { AuthSessionGuestTokenResponse } from 'angular-api-gateway/v1/model/authSessionGuestTokenResponse';
import { WINDOW } from '@lentau/ui-kit/core/tokens/window';
import { CountdownService } from '@lentau/ui-kit/core/services/countdown/countdown.service';
import { CounterCodes } from '@lentau/ui-kit/core/services/countdown/constants';
import { Counter } from '@lentau/ui-kit/core/services/countdown/counter.type';
import { AuthCallAnalyticsService } from '@lentau/auth/analytics/services/auth-call-analytics.service';
import { DEFAULT_PASSPORT_ACCESS_TTL_SEC, getJwtExpiresDate } from '@lentau/auth/shared/jwt-payload.helper';
import { CLEAR_STORAGE_KEYS_AFTER_LOGOUT } from '@lentau/auth/shared/clear-storage-keys/clear-storage-key.token';
import { accessTokenExpiresName } from '@lentau/ssr/shared/const';

const initialState: AuthStateProps = {
  isAuth: null,
  isLogouting: false,
  isLoggedOut: false,
  isPreviouslyRegistered: null,
  sessionToken: null,
  loading: false,
  errorList: [],
  lastPhoneNumber: null,
  currentPhoneNumber: null,
  advCommunications: undefined,
  currentAuthStep: AuthStepEnum.NULL,
  authTokenChecked: false,
  authTokenCheckInProgress: false,
  userAuthorizeType: null,
  authToken: {
    requestState: REQUEST_STATE.NONE,
    accessToken: null,
    refreshToken: null,
    expiresIn: null,
  },
  // GR-3599
  callRequest: {
    requestState: REQUEST_STATE.NONE,
    confirmationPhoneNumber: null,
    qrCodeUri: null,
    lastPhoneNumber: null,
  },
  // GR-2751
  selectFlowActive: false,
  selectAcceptRules: true,
  selectAcceptMarketing: true,
  suppressProfileSelection: false,
};

@Injectable({ providedIn: 'root' })
export class AuthStore {
  private state: SignalState<AuthStateProps> = signalState(initialState);

  private storageCleanupKeys: string[] = inject(CLEAR_STORAGE_KEYS_AFTER_LOGOUT);

  private isLenta: boolean = inject(EnvironmentBrandService).isLenta;
  private authApiService: AuthApiService = inject(AuthApiService);
  private analyticsService: AnalyticsService = inject(AnalyticsService);
  private readonly buildEventCatalogEvent: ReturnType<typeof buildEventCatalogEvent> = buildEventCatalogEvent();
  private analyticsLegacyService: AnalyticsLegacyService = inject(AnalyticsLegacyService);
  private toastService: ToastService = inject(ToastService);
  private sessionStorageService: SessionStorageService = inject(SessionStorageService);
  private localStorageService: LocalStorageService = inject(LocalStorageService);
  private cookieService: CookieFacade = inject(CookieFacade);
  private destroyRef: DestroyRef = inject(DestroyRef);
  private browserApi: BrowserApiSupportService = inject(BrowserApiSupportService);
  private dispatcher: Dispatcher = inject(Dispatcher);
  private authService: AuthService = inject(AuthService);
  private window: Window = inject(WINDOW);
  private countdownService: CountdownService = inject(CountdownService);
  // GR-3599
  private deviceIdService: DeviceIdService = inject(DeviceIdService);
  private authCallAnalyticsService: AuthCallAnalyticsService = inject(AuthCallAnalyticsService);

  public isAuth: Signal<boolean> = this.state.isAuth;
  public isLogouting: Signal<boolean> = this.state.isLogouting;
  public isLoggedOut: Signal<boolean> = this.state.isLoggedOut;
  public sessionToken: Signal<string> = this.state.sessionToken;
  public loading: Signal<boolean> = this.state.loading;
  public errorList: Signal<ResponseErrorListItemProps[]> = this.state.errorList;
  public currentPhoneNumber: Signal<string> = this.state.currentPhoneNumber;
  public advCommunications: Signal<boolean> = this.state.advCommunications;
  public lastPhoneNumber: Signal<string> = this.state.lastPhoneNumber;
  public currentAuthStep: Signal<AuthStepEnum> = this.state.currentAuthStep;
  public authTokenChecked: Signal<boolean> = this.state.authTokenChecked;
  public authTokenCheckInProgress: Signal<boolean> = this.state.authTokenCheckInProgress;
  public userAuthorizeType: Signal<UserAuthorizeType | null> = this.state.userAuthorizeType;
  public isPreviouslyRegistered: Signal<boolean> = this.state.isPreviouslyRegistered;

  public accessToken: Signal<string> = this.state.authToken.accessToken;
  public refreshToken: Signal<string> = this.state.authToken.refreshToken;
  public tokenRequestState: Signal<REQUEST_STATE> = this.state.authToken.requestState;
  public tokenExpiresIn: Signal<Date> = this.state.authToken.expiresIn;

  // GR-3599
  public callRequestState: Signal<REQUEST_STATE> = this.state.callRequest.requestState;
  public callRequestLastPhoneNumber: Signal<string> = this.state.callRequest.lastPhoneNumber;
  public confirmationPhoneNumber: Signal<string> = this.state.callRequest.confirmationPhoneNumber;
  public qrCodeUri: Signal<string> = this.state.callRequest.qrCodeUri;

  // GR-2751
  public selectFlowActive: Signal<boolean> = this.state.selectFlowActive;
  public selectAcceptRules: Signal<boolean> = this.state.selectAcceptRules;
  public selectAcceptMarketing: Signal<boolean> = this.state.selectAcceptMarketing;
  public suppressProfileSelection: Signal<boolean> = this.state.suppressProfileSelection;

  public setAuthStep(currentAuthStep: AuthStepEnum): void {
    patchState(this.state, { currentAuthStep, errorList: [] });
  }

  public setAuthData(currentPhoneNumber: string, advCommunications: boolean): void {
    patchState(this.state, { currentPhoneNumber, advCommunications, errorList: [] });
  }

  public resetPreviouslyRegistered(): void {
    patchState(this.state, { isPreviouslyRegistered: null });
  }

  // GR-2751
  public setSelectFlowActive(selectFlowActive: boolean): void {
    patchState(this.state, { selectFlowActive });
  }

  public setSelectConsent(acceptRules: boolean, acceptMarketing: boolean): void {
    patchState(this.state, {
      selectAcceptRules: acceptRules,
      selectAcceptMarketing: acceptMarketing,
      advCommunications: acceptMarketing,
    });
  }

  public resetSelectFlow(): void {
    patchState(this.state, {
      selectFlowActive: false,
      selectAcceptRules: true,
      selectAcceptMarketing: true,
    });
  }

  public setSuppressProfileSelection(suppressProfileSelection: boolean): void {
    patchState(this.state, { suppressProfileSelection });
  }

  public applySocialSession(
    sessionId: string,
    accessToken: string,
    refreshToken: string,
    expiresInSec: number,
    isNewUser: boolean
  ): void {
    patchState(this.state, {
      suppressProfileSelection: true,
      isAuth: true,
      isPreviouslyRegistered: !isNewUser,
      loading: false,
      authTokenChecked: true,
      authTokenCheckInProgress: false,
      errorList: [],
    });

    this.setSessionToken(sessionId);
    this.setTokensData(refreshToken, accessToken, DateUtils.addSecs(new Date(), expiresInSec));
    this.setAuthStep(AuthStepEnum.NULL);
    this.resetSelectFlow();
    this.dispatcher.dispatch(authSuccessWithToken({ isB2BRegistration: false }));
  }

  // GR-3599
  public callAuthStart(phoneNumber: string, acceptMarketingCommunications?: boolean): void {
    patchState(this.state, {
      callRequest: {
        ...this.state.callRequest(),
        requestState: REQUEST_STATE.LOADING,
        lastPhoneNumber: phoneNumber,
      },
    });

    this.authService
      .callAuthStart({
        ...defaultApiGatewayParams,
        deviceId: this.deviceIdService.getDeviceId(),
        callAuthStartRequest: {
          phoneNumber: `${PHONE_PREFIX}${COUNTRY_CODE}${phoneNumber}`,
          acceptMarketingCommunications,
        },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ parameters }: CallAuthStart200Response) => {
          this.authCallAnalyticsService.confirmNumberStatus('success');
          this.setAuthStep(AuthStepEnum.LOGIN_BY_CALL);

          patchState(this.state, {
            callRequest: { ...this.state.callRequest(), ...parameters, requestState: REQUEST_STATE.SUCCESS },
          });
        },

        error: () => {
          this.authCallAnalyticsService.confirmNumberStatus('error');
          this.redirectFromCallToSms();
        },
      });
  }

  // GR-3599
  public authSessionStart(refresh: string, access: string, passportId: string): void {
    patchState(this.state, {
      callRequest: {
        ...this.state.callRequest(),
        requestState: REQUEST_STATE.LOADING,
      },
    });

    this.authService
      .authSessionStart({
        ...defaultApiGatewayParams,
        deviceId: this.deviceIdService.getDeviceId(),
        passportAccessToken: access,
        // Надо отправлять в base64, dont ask why
        refreshToken: btoa(refresh),
        // Нужен пустой
        authSessionStartRequest: {},
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ sessionId }: AuthSessionGuestTokenResponse) => {
          if (sessionId) {
            if (!passportId || !this.isLenta) this.setAuthStep(AuthStepEnum.NULL);
            this.setSessionToken(sessionId);
            patchState(this.state, {
              isAuth: true,
              callRequest: { ...this.state.callRequest(), requestState: REQUEST_STATE.SUCCESS },
            });
            this.dispatcher.dispatch(authSuccessWithToken({ isB2BRegistration: false }));
            this.toastService.show(new ToasterSuccessMessage('Номер телефона подтверждён'));
          } else {
            this.toastService.show([new ToasterErrorMessage(standartError)]);
            this.redirectFromCallToSms();
          }
        },

        error: (error: unknown) => {
          this.toastService.show([new ToasterErrorMessage(standartError)]);
          this.redirectFromCallToSms();
          this.authCallAnalyticsService.loginError({ errorType: JSON.stringify(error), type: 'call' });
        },
      });
  }

  public getOtpCodePopup(lastPhoneNumber: string, acceptMarketingCommunications: boolean = true): void {
    this.getOtpCode(lastPhoneNumber, OtpCodeTypeEnum.POPUP, acceptMarketingCommunications);
  }

  public getOtpCodeB2B(lastPhoneNumber: string): void {
    this.getOtpCode(lastPhoneNumber, OtpCodeTypeEnum.B2B);
  }

  public getOtpCodeTablet(lastPhoneNumber: string, acceptMarketingCommunications: boolean): void {
    this.getOtpCode(lastPhoneNumber, OtpCodeTypeEnum.TABLET, acceptMarketingCommunications);
  }

  public getOtpCodeReferral(lastPhoneNumber: string, acceptMarketingCommunications: boolean): void {
    this.getOtpCode(lastPhoneNumber, OtpCodeTypeEnum.REFERRAL, acceptMarketingCommunications);
  }

  public logout(): void {
    patchState(this.state, { isLogouting: true, loading: true, isLoggedOut: false });

    this.authApiService
      .logout()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: ResponseAuthLogout) => {
          this.logoutSuccess();
          const sessionToken: string = data?.Head?.SessionToken;
          if (sessionToken && this.browserApi.isBrowser()) {
            this.setSessionToken(sessionToken);
          }
        },
        error: (errorList: ResponseErrorListItemProps[]) =>
          patchState(this.state, { isLogouting: false, loading: false, errorList, isLoggedOut: false }),
      });
  }

  // Только для разлогина на фронте после разлогина на беке де-факто
  public logoutSuccess(): void {
    this.analyticsLegacyService.pushLogoutEvent();
    this.sessionStorageService.setObjectLikeItem(VIEW_STATE, true);
    this.clearLsByKeysAfterLogout();
    this.cookieService.delete(COOKIES_USER_ID_KEY);

    this.setUserAuthorizeType(UserAuthorizeType.GUEST); // обновление стора этим методом перебивается строчкой ниже
    patchState(this.state, {
      ...initialState,
      isAuth: false,
      isLogouting: false,
      isLoggedOut: true,
      authTokenChecked: true,
      userAuthorizeType: UserAuthorizeType.GUEST, // добавлено чтобы корректно сетился гость при разлогине
    });

    this.getPassportGuestToken();
  }

  public authCodeTokenGet(
    phone: string,
    code: string,
    isB2BRegistration: boolean = false,
    isTablet: boolean = false,
    isReferral: boolean = false
  ): void {
    patchState(this.state, { loading: true });

    this.authApiService
      .authCodeTokenGet(phone, code, isTablet, isReferral)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (authResult: AuthResult) => {
          patchState(this.state, { errorList: [], loading: false });

          if (isB2BRegistration) {
            this.analyticsService.pushToDataLayer(this.buildEventCatalogEvent(mapRegistrationStep1CompleteEvent));
          }

          if (authResult?.token) {
            patchState(this.state, { isAuth: true });
            this.syncPassportAfterAuthCodeVerify(authResult);
            this.dispatcher.dispatch(authSuccessWithToken({ isB2BRegistration }));
          }

          // для b2c авторизации добавляем скрытие попапа при успешной авторизации
          if (!authResult?.passportId || !this.isLenta) {
            this.setAuthStep(AuthStepEnum.NULL);
          }
        },

        error: (errorList: ResponseErrorListItemProps[]) => {
          this.analyticsService.pushToDataLayer(this.buildEventCatalogEvent(mapInputCodeErrorEvent));
          if (isTablet || isReferral) {
            this.toastService.show([new ToasterErrorMessage(getErrorMessageFromErrorList(errorList))]);
          } else {
            this.toastService.show([new ToasterInfoMessage(getErrorMessageFromErrorList(errorList))]);
          }
          patchState(this.state, { loading: false, errorList });
        },
      });
  }

  public sessionGet(): void {
    let token: string = this.cookieService.get(COOKIES_TOKEN_SESSION_KEY);
    token = token === 'null' ? null : token;

    patchState(this.state, { authTokenChecked: false, loading: true, authTokenCheckInProgress: true });

    this.authApiService
      .sessionGet(token)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: ResponseGetAuthTokenDto) => {
          let isAuth: boolean = false;

          if (data?.Head?.SessionGroups?.includes(UserRolesEnum.BUYER)) {
            isAuth = !!data.Body.SessionToken?.length;

            if (!isAuth) {
              this.setUserAuthorizeType(UserAuthorizeType.GUEST);
            }
          } else {
            this.setUserAuthorizeType(UserAuthorizeType.GUEST);
          }

          const sessionToken: string = data?.Body?.SessionToken;
          if (sessionToken && this.browserApi.isBrowser()) {
            this.setSessionToken(sessionToken);
          }

          patchState(this.state, { authTokenChecked: true, loading: false, authTokenCheckInProgress: false, isAuth });
        },

        error: () =>
          patchState(this.state, {
            ...initialState,
            isAuth: false,
            authTokenChecked: true,
            userAuthorizeType: UserAuthorizeType.GUEST,
          }),
      });
  }

  public setIsAuthByCache(): void {
    patchState(this.state, { isAuth: true });
  }

  public setUserAuthorizeType(userAuthorizeType: UserAuthorizeType): void {
    patchState(this.state, { userAuthorizeType });
  }

  public getPassportGuestToken(): void {
    patchState(this.state, { authToken: { ...this.state.authToken(), requestState: REQUEST_STATE.LOADING } });
    this.authService
      .getPassportGuestToken(defaultApiGatewayParams)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ accessToken, refreshToken, expiresIn }: GetPassportGuestTokenResponse) =>
          patchState(this.state, {
            authToken: {
              accessToken,
              refreshToken,
              expiresIn: DateUtils.addSecs(new Date(), expiresIn),
              requestState: REQUEST_STATE.SUCCESS,
            },
          }),
        error: () =>
          patchState(this.state, { authToken: { ...this.state.authToken(), requestState: REQUEST_STATE.FAIL } }),
      });
  }

  public refreshPassportToken(): void {
    patchState(this.state, { authToken: { ...this.state.authToken(), requestState: REQUEST_STATE.LOADING } });
    this.authService
      .refreshPassportToken({ ...defaultApiGatewayParams, refreshPassportTokenRequest: {} })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ accessToken, refreshToken, expiresIn }: GetPassportTokenResponse) =>
          patchState(this.state, {
            authToken: {
              accessToken,
              refreshToken,
              expiresIn: DateUtils.addSecs(new Date(), expiresIn),
              requestState: REQUEST_STATE.SUCCESS,
            },
          }),
        error: () => {
          patchState(this.state, {
            authToken: {
              accessToken: null,
              refreshToken: null,
              expiresIn: null,
              requestState: REQUEST_STATE.FAIL,
            },
          });

          this.logoutSuccess();
        },
      });
  }

  public setTokensData(refreshToken: string, accessToken: string, expiresIn: Date): void {
    patchState(this.state, {
      authToken: {
        refreshToken,
        accessToken,
        expiresIn,
        requestState: REQUEST_STATE.SUCCESS,
      },
    });
  }

  /** Keeps PassportExpiresIn in sync after SMS verify so CSR AuthTokenService does not mint guest. */
  private syncPassportAfterAuthCodeVerify(authResult: AuthResult): void {
    const expiresIn: Date =
      getJwtExpiresDate(authResult.jwt?.access) ?? DateUtils.addSecs(new Date(), DEFAULT_PASSPORT_ACCESS_TTL_SEC);

    this.cookieService.set(accessTokenExpiresName, expiresIn.getTime().toString(), {
      expires: DateUtils.addDays(new Date(), 30),
      path: '/',
    });

    if (authResult.jwt?.access && authResult.jwt?.refresh) {
      this.setTokensData(authResult.jwt.refresh, authResult.jwt.access, expiresIn);
      return;
    }

    this.setTokensData(this.state.authToken().refreshToken, this.state.authToken().accessToken, expiresIn);
  }

  // GR-3599
  public redirectFromCallToSms(): void {
    patchState(this.state, {
      callRequest: {
        ...this.state.callRequest(),
        requestState: REQUEST_STATE.FAIL,
        confirmationPhoneNumber: null,
        qrCodeUri: null,
      },
    });
    this.setAuthStep(AuthStepEnum.LOGIN_BY_PHONE);
    this.getOtpCodePopup(this.currentPhoneNumber(), this.advCommunications());
  }

  private getOtpCode(lastPhoneNumber: string, type: OtpCodeTypeEnum, acceptMarketingCommunications?: boolean): void {
    patchState(this.state, { lastPhoneNumber, loading: true });
    this.authApiService
      .authCodeSend(lastPhoneNumber, acceptMarketingCommunications, type === OtpCodeTypeEnum.TABLET)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ sent, timeout, isUserNew }: SendAuthCodeResponse) => {
          patchState(this.state, { errorList: [], loading: false });

          patchState(this.state, { isPreviouslyRegistered: !isUserNew });
          if (isUserNew && type !== OtpCodeTypeEnum.TABLET && type !== OtpCodeTypeEnum.REFERRAL) {
            this.registrationSuccess();
          }

          if (sent || timeout) this.setAuthStepByOtpCode(type);

          if (timeout) {
            const counter: Counter = this.countdownService.getCounter(CounterCodes.CodeConfirmation);
            counter.start();
          }
        },

        error: (errorList: ResponseErrorListItemProps[]) => {
          this.analyticsLegacyService.pushOtpCodeFailEvent();
          patchState(this.state, { errorList, loading: false });
          throw errorList;
        },
      });
  }

  private registrationSuccess(): void {
    this.toastService.show([new ToasterSuccessMessage('Спасибо за регистрацию')]);
  }

  private setAuthStepByOtpCode(type: OtpCodeTypeEnum): void {
    const currentAuthStep: AuthStepEnum = authStepByOtpCodeType.get(type);

    if (currentAuthStep) this.setAuthStep(currentAuthStep);
  }

  private setSessionToken(sessionToken: string): void {
    patchState(this.state, { sessionToken });

    if (this.browserApi.isBrowser()) {
      this.cookieService.set(COOKIES_TOKEN_SESSION_KEY, sessionToken, {
        path: '/',
        domain: this.window.location.hostname.split('.').slice(-2).join('.'),
        expires: DateUtils.addDays(new Date(), COOKIES_TOKEN_SESSION_KEY_EXPIRES),
      });
    }
  }

  private clearLsByKeysAfterLogout(): void {
    this.storageCleanupKeys.forEach((key: string) => this.localStorageService.removeItemByKey(key));
  }
}
